import {
  SESSION_SLICE_SESSION_STORAGE_KEY,
  axiosInstance,
  useDispatch,
  useSelector,
  updateToken,
  updateRefreshToken,
  setTokenExpiresAtMs,
  logoutAuthUser,
  LoginTypeEnum,
  getLoggingActions,
  UserTypeEnum,
  performMsBackendTokenRefresh,
  getResolvedAuthProvider,
  normalizeSessionToken,
  expiresInToMs,
  computeProactiveRefreshDelayMs,
} from '@app-store'

import { AUTH_PROVIDER, NO_TENANT_HEADER_API } from './constants'
import { environment } from './environment'
import type { InternalAxiosRequestConfig } from 'axios'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { jwtDecode } from 'jwt-decode'
import { recordSessionActivity } from '@ecrm-session'
import { getPostLogoutCapturePath } from './postLogoutReturnPath'

import { store, persist } from './store'
import { clearMsalAppSessionOnly, msalInstance } from './msalConfig'
import { clearMsalAccessTokenBridge } from '@ecrm-msal'
import { clearSessionLastTenantId, getTenantIdFromWindow } from '@app-store'

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()

type AuthAwareRequestConfig = InternalAxiosRequestConfig & {
  skipAuthorization?: boolean
}

export { store, persist }

// ──────────────────────────────────────────────
// Axios Request Interceptor
// Bearer token comes from Redux `session` (sessionStorage via redux-persist).
// ──────────────────────────────────────────────
axiosInstance.interceptors.request.use(function (
  config: InternalAxiosRequestConfig
) {
  const authConfig = config as AuthAwareRequestConfig
  const tenantId = getTenantIdFromWindow()
  const token = store.getState().session.token
  const configUrl: string = config.url ?? ''
  const skipAuthorization = authConfig.skipAuthorization === true

  // Keep this custom policy flag inside the app; it must not reach the adapter.
  if (skipAuthorization) {
    delete authConfig.skipAuthorization
    delete config.headers.Authorization
  }

  if (
    !NO_TENANT_HEADER_API.some((url) => configUrl?.includes(url)) &&
    tenantId != null &&
    !Object.prototype.hasOwnProperty.call(config.headers, 'X-TenantId')
  ) {
    config.headers['X-TenantId'] = tenantId === -1 ? '' : tenantId
  }

  if (token && !skipAuthorization) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  const markUser = config.countsAsUserActivity === true
  if (markUser) {
    delete config.countsAsUserActivity
  }
  if (markUser && token) {
    recordSessionActivity()
  }

  return config
})

// ──────────────────────────────────────────────
// Utility: Add dynamic params to login calls
// ──────────────────────────────────────────────
const dynamicParamConfig = (
  req: { url: string },
  newSource: string,
  userType?: string
) => {
  try {
    if (!req.url.includes('/v1/users/login')) return
    const base = req.url.startsWith('http')
      ? req.url
      : `${window.location.origin}${req.url}`
    const url = new URL(base)
    url.searchParams.set('action', newSource)

    if (userType) {
      url.searchParams.set('user_type', userType)
    }

    req.url = url.href.startsWith(window.location.origin)
      ? url.href.replace(window.location.origin, '')
      : url.href
  } catch (err) {
    console.error('Failed to update source param:', err)
  }
}

// ──────────────────────────────────────────────
// MSAL Singleton Setup
// ──────────────────────────────────────────────

// Initialize early
msalInstance.initialize().catch((e) => console.warn('MSAL init error:', e))

// ──────────────────────────────────────────────
// Token Refresh Concurrency Lock
// ──────────────────────────────────────────────
let tokenRefreshPromise: Promise<string | null> | null = null

// ──────────────────────────────────────────────
// MS-Backend: whether a 403 triggers the same refresh-and-retry as a 401
// ──────────────────────────────────────────────
// A real 401 reliably means "your token expired" — always worth a refresh.
// A 403 is murkier: it can mean the same expired/invalid-token case (per the
// API Gateway authorizer's own semantics — see the interceptor condition
// below), or it can mean a genuine, backend-confirmed permission denial
// (the session is perfectly valid, the user just lacks access to this one
// endpoint) — refreshing does nothing to fix that second case, and an
// endpoint that's called unconditionally and permission-denied for a whole
// class of users (e.g. entities/bookmark for standard users) can rack up
// enough independent refresh cycles to eventually trip a genuine refresh
// failure and the redirect-to-login fallback — exactly what a permission gap
// should never cause.
//
// The two cases are distinguishable by response body: a genuine backend
// permission denial comes back with this exact structured shape —
//   { "code": "403 FORBIDDEN", "message": "User is not authorized..." }
// — because it's the app's own access-denied handler responding, not the API
// Gateway authorizer rejecting the request before it ever reaches the app.
// So: only treat a 403 as refresh-worthy when its body does NOT have this
// exact code. Any 403 that DOES carry this code is a confirmed permission
// denial — never refresh for it, just let it reject normally.
const CONFIRMED_FORBIDDEN_CODE = '403 FORBIDDEN'

function isConfirmedPermissionDenial(error: {
  response?: { data?: unknown }
}): boolean {
  const data = error.response?.data
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).code === CONFIRMED_FORBIDDEN_CODE
  )
}

// Master kill-switch: flip to `false` to stop 403 from ever triggering
// refresh at all, regardless of body shape (401 handling is unaffected
// either way). Layered UNDER isConfirmedPermissionDenial above — with this
// `true`, a 403 still only refreshes when it's NOT a confirmed denial.
const TREAT_403_AS_REFRESHABLE = true

// ──────────────────────────────────────────────
// Session Expiry Handler
// ──────────────────────────────────────────────
export const handleSessionExpiry = async (reason: string) => {
  console.error(`Session expired: ${reason}. Logging out...`)
  tokenRefreshPromise = null
  const token = store.getState().session.token
  if (token) {
    await getLoggingActions(
      token,
      LoginTypeEnum.ADMIN_FORCED_LOGOUT,
      UserTypeEnum.AGENT
    )
  }
  store.dispatch(logoutAuthUser())
  const resolvedProvider = getResolvedAuthProvider() ?? environment.authProvider
  if (resolvedProvider === AUTH_PROVIDER.MSAL) {
    await clearMsalAppSessionOnly()
    clearMsalAccessTokenBridge()
  }
  clearSessionLastTenantId()
  await persist.flush()
  try {
    sessionStorage.removeItem(SESSION_SLICE_SESSION_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  const capture = getPostLogoutCapturePath()
  const qs = new URLSearchParams()
  if (resolvedProvider === AUTH_PROVIDER.MSAL) {
    qs.set('forceMsalPromptNextLogin', '1')
  }
  if (capture) {
    qs.set('returnPath', capture)
  }
  const q = qs.toString()
  window.location.href = q
    ? `${window.location.origin}/logout?${q}`
    : `${window.location.origin}/logout`
}

// ──────────────────────────────────────────────
// MSAL Token Refresh Logic
// ──────────────────────────────────────────────
const refreshMSALToken = async (): Promise<string | null> => {
  console.log('Starting MSAL token refresh...')

  try {
    const accounts = msalInstance.getAllAccounts()
    console.log('MSAL accounts found:', accounts.length)

    if (!accounts || accounts.length === 0) {
      handleSessionExpiry('No MSAL accounts found in cache')
      return null
    }

    const account = msalInstance.getActiveAccount() ?? accounts[0]
    const request = {
      scopes: [
        'openid',
        'offline_access',
        'email',
        'profile',
        environment.msalConfig.msalScope,
      ],
      account,
      forceRefresh: false,
    }

    try {
      const response = await msalInstance.acquireTokenSilent(request)
      if (response && response.accessToken) {
        console.log('Token acquired silently, updating store')
        await getLoggingActions(
          response.accessToken,
          LoginTypeEnum.REFRESH,
          UserTypeEnum.AGENT
        )
        store.dispatch(updateToken({ token: response.accessToken }))
        return response.accessToken
      }
      console.warn('acquireTokenSilent returned no accessToken', response)
    } catch (err) {
      console.warn('acquireTokenSilent failed:', err)
      // Try to type-narrow err as best as possible rather than using 'any'
      // Most MSAL errors should be objects at runtime
      const errObj = err as { errorCode?: string; name?: string }
      const errCode = errObj?.errorCode || errObj?.name || ''
      const needsInteraction =
        err instanceof InteractionRequiredAuthError ||
        [
          'interaction_required',
          'login_required',
          'monitor_window_timeout',
          'consent_required',
        ].includes(errCode)

      if (!needsInteraction) {
        handleSessionExpiry('Token acquisition failed: ' + String(err))
        return null
      }

      console.log('Interaction required, attempting popup token acquisition...')
      try {
        console.log('Attempting popup token acquisition...')
        const popupResponse = await msalInstance.acquireTokenPopup(request)
        if (popupResponse && popupResponse.accessToken) {
          console.log('Token acquired via popup, updating store')
          await getLoggingActions(
            popupResponse.accessToken,
            LoginTypeEnum.REFRESH,
            UserTypeEnum.AGENT
          )
          store.dispatch(updateToken({ token: popupResponse.accessToken }))
          return popupResponse.accessToken
        } else {
          console.warn('Popup returned no token, fallback to redirect...')
          await msalInstance.acquireTokenRedirect(request)
          return null
        }
      } catch (popupErr) {
        console.error('Popup token acquisition failed:', popupErr)
        try {
          await msalInstance.acquireTokenRedirect(request)
          return null
        } catch (redirErr) {
          console.error('Redirect fallback failed:', redirErr)
          handleSessionExpiry(
            'Interactive token acquisition failed: ' + String(redirErr)
          )
          return null
        }
      }
    }

    return null
  } catch (e) {
    console.error('Token refresh failed with exception:', e)
    handleSessionExpiry('Token refresh exception: ' + String(e))
    return null
  }
}

// ──────────────────────────────────────────────
// MS-Backend Token Refresh Logic (Flow 3)
// Goes through the same shared axiosInstance as everything else (via
// msBackendAuth.api.ts's authRequest, withCredentials: true for the cookie).
// The response interceptor below explicitly excludes /public/api/v1/auth/** from
// the MS_BACKEND retry branch so this call's own 401s can't re-enter it and loop.
// ──────────────────────────────────────────────
const refreshMsBackendToken = async (): Promise<string | null> => {
  // Captured before any await so a concurrent logout (which bumps authEpoch via
  // clearSession) can be detected once this call resolves — see
  // SessionState.authEpoch. Without this, a refresh already in flight when the
  // user logged out could resurrect the session (on success) or bounce them to
  // MS login a second time (on failure) after logout already handled it.
  const epochAtStart = store.getState().session.authEpoch ?? 0

  // A refresh here is always REACTIVE — a business call got a 401/403 mid-session.
  // On a confirmed dead session we don't silently bounce through Microsoft again
  // (risking a loop with no explanation); we clear local state and land on the
  // existing "Access Denied" page, whose manual retry button lets the user decide.
  const landOnAccessDenied = () => {
    store.dispatch(logoutAuthUser())
    window.location.href = `${window.location.origin}/error/user-not-found`
  }

  try {
    // Cookie + CSRF + Origin prove the caller now — no bearer argument.
    const res = await performMsBackendTokenRefresh()

    if ((store.getState().session.authEpoch ?? 0) !== epochAtStart) {
      // Superseded by a newer auth cycle (e.g. logout) while this was in flight.
      return null
    }

    if (!res) {
      // null = confirmed 401 — the session/refresh is genuinely gone.
      console.info(
        '[msBackendAuth] refresh rejected (401) — landing on Access Denied'
      )
      landOnAccessDenied()
      return null
    }

    // Rotate the access token + expiry. The expiry update is load-bearing: the
    // proactive scheduler re-schedules off tokenExpiresAtMs, so a stale value
    // would make it fire against a past instant in a tight loop. (No refresh
    // token to store — refresh is cookie-based now.)
    const { accessToken, expiresIn } = normalizeSessionToken(res)
    if (!accessToken) {
      // Boundary validation: a 2xx refresh with no usable token is malformed —
      // treat it like a dead session rather than rotating in an empty bearer.
      console.info(
        '[msBackendAuth] refresh returned no token — landing on Access Denied'
      )
      landOnAccessDenied()
      return null
    }
    console.info('[msBackendAuth] refresh succeeded — token rotated')
    store.dispatch(updateToken({ token: accessToken }))
    store.dispatch(setTokenExpiresAtMs(expiresInToMs(expiresIn)))
    return accessToken
  } catch {
    if ((store.getState().session.authEpoch ?? 0) !== epochAtStart) {
      return null
    }
    // A thrown error here is a network / CORS / 5xx failure — NOT proof the
    // session is dead. Don't force logout: log and leave the session and page
    // untouched so a transient blip (or the known edge-CORS issue) can't bounce
    // the user out. A genuinely expired session surfaces as `!res` (401) above.
    console.info(
      '[msBackendAuth] refresh failed (network/CORS/5xx) — leaving session intact'
    )
    return null
  }
}

// Single-flight wrapper shared by the reactive interceptor branch below and
// the proactive expiry scheduler: however many callers ask concurrently, only
// one /auth/refresh request is in flight, and everyone awaits its result.
const runSingleFlightMsBackendRefresh = (): Promise<string | null> => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = refreshMsBackendToken().finally(() => {
      tokenRefreshPromise = null
    })
  }
  return tokenRefreshPromise
}

// ──────────────────────────────────────────────
// Proactive MS-backend token refresh scheduler
// Fires PROACTIVE_REFRESH_SKEW_MS before tokenExpiresAtMs instead of waiting
// for a request to 401/403. Cancels/reschedules whenever the token changes
// (new login, reactive refresh, logout). Timer dies with the page on unload.
// ──────────────────────────────────────────────
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null
let proactiveScheduledForKey: string | null = null

const syncProactiveMsBackendRefresh = () => {
  const { token, tokenExpiresAtMs } = store.getState().session
  const provider = getResolvedAuthProvider() ?? environment.authProvider

  if (provider !== AUTH_PROVIDER.MS_BACKEND || !token) {
    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer)
      proactiveRefreshTimer = null
      proactiveScheduledForKey = null
      console.info('[msBackendAuth] proactive refresh cancelled (no session)')
    }
    return
  }

  // Key on the token AND the expiry, not the token alone.
  //
  // refreshMsBackendToken dispatches the new token and the new expiry as TWO
  // separate actions, and store.subscribe runs after each. That leaves an
  // intermediate state holding the NEW token with the OLD, already-imminent
  // expiry. Keyed on the token alone, that state armed a timer at the 5s floor
  // — and the very next dispatch, the one carrying the correct expiry, was then
  // skipped as "already scheduled for this token", so the bad timer survived.
  // Net effect: a successful refresh every 5 seconds, indefinitely, with every
  // call returning 200 so it never looked like a failure.
  //
  // Including the expiry means neither dispatch can be skipped, and the final
  // schedule is always computed from the expiry that actually applies.
  const scheduleKey = `${token}|${tokenExpiresAtMs ?? ''}`
  if (scheduleKey === proactiveScheduledForKey) return

  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer)
  proactiveScheduledForKey = scheduleKey

  const delay = computeProactiveRefreshDelayMs(tokenExpiresAtMs, Date.now())
  if (delay == null) {
    // No expiry known for this token — nothing to schedule; the reactive
    // 401/403 interceptor path still covers it.
    proactiveRefreshTimer = null
    return
  }

  console.info(
    `[msBackendAuth] proactive refresh scheduled in ${Math.round(
      delay / 1000
    )}s`
  )
  proactiveRefreshTimer = setTimeout(() => {
    proactiveRefreshTimer = null
    proactiveScheduledForKey = null
    console.info('[msBackendAuth] proactive refresh firing')
    void runSingleFlightMsBackendRefresh()
    // On success updateToken/setTokenExpiresAtMs change the store, and the
    // subscription below reschedules for the new token automatically.
  }, delay)
}

store.subscribe(syncProactiveMsBackendRefresh)
syncProactiveMsBackendRefresh()

// ──────────────────────────────────────────────
// Axios Response Interceptor (Token Refresh Logic)
// ──────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    if (!originalRequest) return Promise.reject(error)

    type CustomAxiosRequestConfig = typeof originalRequest & {
      _retry?: boolean
    }
    const customRequest = originalRequest as CustomAxiosRequestConfig

    const authBearerToken =
      originalRequest?.headers?.Authorization?.replace('Bearer ', '') ?? ''

    if (
      isTokenExpired(
        store.getState().session.refreshToken ?? authBearerToken
      ) &&
      !customRequest._retry &&
      (getResolvedAuthProvider() ?? environment.authProvider) ===
        AUTH_PROVIDER.MSAL
    ) {
      console.info('Access token expired, triggering MSAL refresh...')
      customRequest._retry = true

      try {
        if (tokenRefreshPromise) {
          console.log('Token refresh already in progress, awaiting...')
          const newToken = await tokenRefreshPromise
          if (newToken) {
            dynamicParamConfig(
              customRequest,
              LoginTypeEnum.UNAUTHORIZED,
              UserTypeEnum.AGENT
            )
            customRequest.headers['Authorization'] = `Bearer ${newToken}`
            return axiosInstance({ ...customRequest, params: undefined })
          } else return Promise.reject(error)
        }

        console.log('Starting new token refresh...')
        tokenRefreshPromise = refreshMSALToken()
        const newToken = await tokenRefreshPromise

        if (newToken) {
          dynamicParamConfig(
            customRequest,
            LoginTypeEnum.UNAUTHORIZED,
            UserTypeEnum.AGENT
          )
          customRequest.headers['Authorization'] = `Bearer ${newToken}`
          return axiosInstance({ ...customRequest, params: undefined })
        } else {
          console.error('Token refresh failed, rejecting request')
          return Promise.reject(error)
        }
      } finally {
        tokenRefreshPromise = null
        console.log('Token refresh lock cleared')
      }
    }

    // MS-backend token refresh — single-flight via shared lock.
    // Accepts both 401 and 403: an expired/invalid Entra access token is
    // rejected by the API Gateway Lambda authorizer (JWKS check happens
    // there, not in this app — see LocalTokenVerifier), and API Gateway
    // maps an authorizer Deny to 403, not 401. Only a literal "Unauthorized"
    // thrown by the authorizer maps to 401, so 403 is the common case here.
    //
    // Excludes /public/api/v1/auth/** (the auth endpoints themselves, called via
    // this same shared axiosInstance from msBackendAuth.api.ts): a 401 from
    // /auth/refresh is an expected outcome (expired refresh session), not a
    // signal to refresh again — retrying it here would re-enter this same
    // interceptor from inside the refresh call already in flight, deadlocking
    // on tokenRefreshPromise instead of ever resolving it.
    if (
      (error.response?.status === 401 ||
        (error.response?.status === 403 &&
          TREAT_403_AS_REFRESHABLE &&
          !isConfirmedPermissionDenial(error))) &&
      !customRequest._retry &&
      !customRequest.url?.includes('/public/api/v1/auth') &&
      (getResolvedAuthProvider() ?? environment.authProvider) ===
        AUTH_PROVIDER.MS_BACKEND
    ) {
      customRequest._retry = true
      // Single-flight: concurrent 401/403s all await the same refresh —
      // exactly one /auth/refresh goes out, then each queued request retries
      // once with the new token (the _retry flag above prevents any second
      // retry of the same request, so no refresh-and-retry loop is possible).
      const newToken = await runSingleFlightMsBackendRefresh()
      if (newToken) {
        customRequest.headers['Authorization'] = `Bearer ${newToken}`
        return axiosInstance({ ...customRequest, params: undefined })
      }
      return Promise.reject(error)
    }

    // Cognito fallback
    if (
      (error.response?.status === 409 || error.response?.status === 403) &&
      !customRequest._retry &&
      (getResolvedAuthProvider() ?? environment.authProvider) ===
        AUTH_PROVIDER.COGNITO
    ) {
      customRequest._retry = true
      const refreshToken = store.getState().session.refreshToken
      const clientId = environment.msalConfig.clientId
      const loginUrl = environment.msalConfig.authorityUrl
      if (checkIfRefreshTokenExist(refreshToken, clientId, loginUrl)) {
        try {
          const params = new URLSearchParams()
          params.append('grant_type', 'refresh_token')
          params.append('client_id', clientId)
          params.append('refresh_token', refreshToken as string)
          const tokenResponse = await fetch(`${loginUrl}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params.toString(),
          })
          if (tokenResponse.ok) {
            const data = await tokenResponse.json()
            if (data.access_token) {
              store.dispatch(updateToken({ token: data.access_token }))
              storeRefreshTokenInRedux(data)
              customRequest.headers[
                'Authorization'
              ] = `Bearer ${data.access_token}`
              return axiosInstance({ ...customRequest, params: undefined })
            }
          }
        } catch (e) {
          console.error('Cognito token refresh error:', e)
        }
      }
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    )
  }
)

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────
const checkIfRefreshTokenExist = (
  refreshToken: string | null | undefined,
  clientId: string,
  loginUrl: string
) => {
  return refreshToken && clientId && loginUrl
}

const storeRefreshTokenInRedux = (data: {
  refresh_token: { refreshToken: string }
}) => {
  if (data.refresh_token) store.dispatch(updateRefreshToken(data.refresh_token))
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token)
    const currentTime = Date.now() / 1000
    return typeof decoded.exp === 'number' ? decoded.exp < currentTime : false
  } catch (error) {
    console.error('Failed to decode token:', error)
    return true
  }
}
